import type { FC, InputHTMLAttributes, ReactNode } from "react";
import { LuCloudUpload as LuUploadCloud } from "react-icons/lu";

import { cn } from "@/utils/lib";

type InputUploadProps = {
  title: ReactNode;
  caption?: ReactNode;
  icon?: ReactNode;
  size?: "sm" | "md" | "lg";
  className?: string;
  showInput?: boolean;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "title" | "size">;

const InputUpload: FC<InputUploadProps> = ({
  title,
  caption = "Supports jpg, jpeg, fig, png with max size: 1mb",
  icon = <LuUploadCloud />,
  size = "md",
  className,
  showInput = true,
  ...props
}) => {
  const paddingClass = size === "sm" ? "p-4" : size === "lg" ? "p-12" : "p-8";

  return (
    <div
      data-element="input-upload"
      className={cn(
        `
          relative flex w-full cursor-pointer flex-col items-center
          justify-center gap-2 rounded-md border border-transparent bg-card
          text-center text-muted ring-offset-background transition-colors
          hover:border-border hover:bg-background hover:text-muted-foreground
          focus-visible:ring-2 focus-visible:ring-ring
          focus-visible:ring-offset-2 focus-visible:outline-hidden
        `,
        paddingClass,
        className,
      )}
    >
      {icon && <div className="text-4xl text-foreground">{icon}</div>}
      <div>
        <span className="font-sans font-semibold text-muted-foreground">
          {title}
        </span>
        {caption && (
          <span className="mt-2 block text-xs text-muted">{caption}</span>
        )}
      </div>
      {showInput && (
        <input
          {...props}
          type="file"
          className="absolute inset-0 cursor-pointer opacity-0"
        />
      )}
    </div>
  );
};

export default InputUpload;
